import { html, LitElement, PropertyValues } from 'lit';
import { property, state } from 'lit/decorators.js';
import { CalendarUtility } from './calendar-utility';
import { DateUtility } from './date-utility';
import { LocaleCode } from '../../types/lang.types';
import {
    CalendarMode,
    ClassBuilder,
    SelectOption,
    Sheets,
    SingleDateChange,
    RangeDateChange,
} from '../../types/calendar.types';

export enum CalendarTimeElementClass {
    Day = 'c-calendar__day',
    DayNumber = 'c-calendar__day-number',
    Today = 'c-calendar__day--today',
    Selected = 'c-calendar__day--selected',
    SelectedStart = 'c-calendar__day--selected-start',
    SelectedEnd = 'c-calendar__day--selected-end',
    Unselectable = 'c-calendar__day--unselectable',
    Flexible = 'c-calendar__day--flexible',
    FlexibleStart = 'c-calendar__day--flexible-start',
    FlexibleEnd = 'c-calendar__day--flexible-end',
    SelectedRange = 'c-calendar__day--selected-range',
}

export class Calendar extends LitElement {
    protected override createRenderRoot(): HTMLElement | DocumentFragment {
        return this; // Light DOM – render inside parent's Shadow DOM
    }

    @property({ type: String })
    public lang = LocaleCode.DE_CH;

    @property({ type: String })
    public currency = 'CHF';

    @property({ type: String })
    public brand = 'kuoni';

    @property({ type: String })
    public tenant = 'kuonireisen-ch';

    @property({ type: Object, attribute: 'additional-tracking-data' })
    public additionalTrackingData = {};

    @property({ type: Number, attribute: 'booking-day-offset' })
    bookingDayOffset = 0;

    @property({
        type: Number,
        attribute: 'sheets',
        converter: (value: string | null): 1 | 2 => (Number(value) === 2 ? 2 : 1),
    })
    sheets: Sheets.Single | Sheets.Double = Sheets.Single;

    @property({ type: String, attribute: 'flexible-end-date' })
    flexibleEndDate = '';

    @property({ type: String, attribute: 'selected-end-date' })
    selectedEndDate = '';

    @property({ type: String, attribute: 'flexible-start-date' })
    flexibleStartDate = '';

    @property({ type: String, attribute: 'selected-start-date' })
    selectedStartDate = '';

    @property({
        type: Boolean,
        attribute: 'month-selection-dropdown',
        converter: (value: string | null): boolean => value !== null && value !== 'false',
    })
    monthSelectionDropdown = false;

    @property({ type: Number, attribute: 'month-dropdown-limit' })
    monthDropdownLimit = 16;

    @property({ type: String, attribute: 'mode' })
    mode: CalendarMode = CalendarMode.Single;

    @property({ type: String, attribute: 'selected-date' })
    selectedDate = '';

    @property({ type: Array, attribute: 'selected-dates' })
    selectedDates: string[] = [];

    @property({ type: Array })
    private openingWeekdays: number[] = [1, 2, 3, 4, 5, 6, 0];

    @property({ type: Array })
    specialSelectableDates: string[] = [];

    @state() protected activeMonth: number;
    @state() protected activeYear: number;
    @state() private selecting: 'start' | 'end' = 'start';
    @state() private today = new Date();
    @state() private todayString = '';
    @state() private prevDisabled = false;
    @state() private nextDisabled = false;

    private todayUpdateTimer?: number;

    constructor() {
        super();
        const today = new Date();
        this.activeMonth = today.getMonth() + 1;
        this.activeYear = today.getFullYear();
        this.updateTodayCache();
    }

    reset() {
        if (this.selectedDate === '') {
            return;
        }
        this.selectedDate = '';
        // TODO check and refactor this event!
        this.dispatchEvent(
            new CustomEvent('der.change', {
                detail: {
                    mode: this.mode,
                    date: '',
                } as SingleDateChange,
                bubbles: true,
                composed: true,
            })
        );
    }

    override connectedCallback() {
        super.connectedCallback();
        this.addEventListener('keydown', this.onKeydown);
        CalendarUtility.validateModeProperties(
            this.mode,
            this.selectedDate,
            this.selectedStartDate,
            this.selectedEndDate
        );
    }

    override disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('keydown', this.onKeydown);

        if (this.todayUpdateTimer) {
            clearTimeout(this.todayUpdateTimer);
            this.todayUpdateTimer = undefined;
        }
    }

    protected override willUpdate(changedProperties: PropertyValues) {
        if (changedProperties.has('mode')) {
            CalendarUtility.validateModeProperties(
                this.mode,
                this.selectedDate,
                this.selectedStartDate,
                this.selectedEndDate
            );
        }
    }

    protected override updated(changedProperties: PropertyValues) {
        if (changedProperties.has('selectedStartDate') && this.selectedStartDate && this.mode === CalendarMode.Range) {
            if (DateUtility.isValidDateString(this.selectedStartDate) && this.sheets === 1) {
                this.activeMonth = new Date(this.selectedStartDate).getMonth() + 1;
                this.activeYear = new Date(this.selectedStartDate).getFullYear();
            }
        }

        if (changedProperties.has('selectedDate') && this.selectedDate && this.mode === CalendarMode.Single) {
            if (DateUtility.isValidDateString(this.selectedDate) && this.sheets === 1) {
                this.activeMonth = new Date(this.selectedDate).getMonth() + 1;
                this.activeYear = new Date(this.selectedDate).getFullYear();
            }
        }
    }

    private updateTodayCache() {
        this.today = new Date();
        this.todayString = DateUtility.getDateString(DateUtility.getIsoDate(this.today));

        const msUntilMidnight = DateUtility.getMillisecondsUntilMidnight(this.today);
        this.todayUpdateTimer = window.setTimeout(() => {
            this.updateTodayCache();
            this.requestUpdate();
        }, msUntilMidnight);
    }

    private handleSingleDateClick(clickedDate: string): void {
        this.selectedDate = clickedDate;

        // TODO check and refactor this event!
        this.dispatchEvent(
            new CustomEvent('der.change', {
                detail: {
                    mode: this.mode,
                    date: this.selectedDate.split('-').reverse().join('.'), // Convert ISO to German date string
                } as SingleDateChange,
                bubbles: true,
                composed: true,
            })
        );
    }

    private handleRangeDateClick(clickedDate: string): void {
        if (this.selecting === 'start') {
            this.selectedStartDate = clickedDate;
            this.selectedEndDate = clickedDate;
            this.selecting = 'end';
        } else if (this.selecting === 'end') {
            this.selectedEndDate = clickedDate;

            try {
                const start = new Date(
                    DateUtility.getYearAsNumber(this.selectedStartDate),
                    DateUtility.getMonthAsNumber(this.selectedStartDate) - 1,
                    DateUtility.getDayAsNumber(this.selectedStartDate)
                );
                const end = new Date(
                    DateUtility.getYearAsNumber(this.selectedEndDate),
                    DateUtility.getMonthAsNumber(this.selectedEndDate) - 1,
                    DateUtility.getDayAsNumber(this.selectedEndDate)
                );

                if (start > end) {
                    const oldSelectedStartDate = this.selectedStartDate;
                    this.selectedStartDate = this.selectedEndDate;
                    this.selectedEndDate = oldSelectedStartDate;
                }

                // TODO check and refactor this event!
                this.dispatchEvent(
                    new CustomEvent('der.change', {
                        detail: {
                            mode: this.mode,
                            start: this.selectedStartDate,
                            end: this.selectedEndDate,
                        } as RangeDateChange,
                        bubbles: true,
                        composed: true,
                    })
                );
            } catch (error) {
                console.error('Calendar: Error processing range dates:', error);
            }

            this.selecting = 'start';
        }
    }

    private _formatDate(day: number, month: number, year: number): string {
        return year + '-' + DateUtility.addLeadingZero(month) + '-' + DateUtility.addLeadingZero(day);
    }

    private handleDayClick(day: number, month: number, year: number): void {
        const clickedDate = this._formatDate(day, month, year);

        if (!DateUtility.isValidDateString(clickedDate)) {
            console.error('Calendar: Invalid clicked date:', clickedDate);
            return;
        }

        if (this.mode === 'single') {
            this.handleSingleDateClick(clickedDate);
        } else {
            this.handleRangeDateClick(clickedDate);
        }
    }

    private onKeydown = (e: KeyboardEvent) => {
        if (e.code !== 'Space' && e.code !== 'Enter') {
            return;
        }

        const timeElement = (e.target as HTMLElement).closest('time') as HTMLTimeElement | null;
        if (timeElement?.dateTime) {
            try {
                const dateData = new Date(timeElement.dateTime);

                if (!timeElement.classList.contains(CalendarTimeElementClass.Unselectable)) {
                    this.handleDayClick(dateData.getDate(), dateData.getMonth() + 1, dateData.getFullYear());
                    e.preventDefault();
                }
            } catch {
                return;
            }
        }
    };

    private nextMonth = (): void => {
        this.activeMonth = this.activeMonth === 12 ? 1 : this.activeMonth + 1;
        this.activeYear = this.activeMonth === 1 ? this.activeYear + 1 : this.activeYear;
    };

    private prevMonth = (): void => {
        this.activeMonth = this.activeMonth === 1 ? 12 : this.activeMonth - 1;
        this.activeYear = this.activeMonth === 12 ? this.activeYear - 1 : this.activeYear;
    };

    protected dayRenderer = (day: number | null, isUnselectable: boolean): unknown => {
        return html`<span class="c-calendar__day-number" tabindex="${isUnselectable ? '-1' : '0'}">${day}</span>`;
    };

    private renderDay(day: number | null, month: number, year: number) {
        if (!day) {
            return html` <div></div>`;
        }

        try {
            const dayClassBuilder = this.getCustomTimeElementClassBuilders(day, month, year);
            const dayClasses = CalendarUtility.getClasses(dayClassBuilder);
            const dateValue = this._formatDate(day, month, year);
            const isUnselectable = dayClasses.includes(CalendarTimeElementClass.Unselectable);
            const isSelectedStart = dayClasses.includes(CalendarTimeElementClass.SelectedStart);
            const isSelectedEnd = dayClasses.includes(CalendarTimeElementClass.SelectedEnd);
            const isSelected = dayClasses.includes(CalendarTimeElementClass.Selected);
            const isToday = dayClasses.includes(CalendarTimeElementClass.Today);
            const showSelectedCircle = isSelectedStart || isSelectedEnd || isSelected;

            return html`
                <time
                    class="${dayClasses.join(' ')}"
                    role="gridcell"
                    aria-selected="${showSelectedCircle}"
                    aria-current="${isToday ? 'date' : 'false'}"
                    aria-label="${DateUtility.formatDateForScreenReader(day, month, year, this.lang)}"
                    datetime="${dateValue}"
                    @click="${() => !isUnselectable && this.handleDayClick(day, month, year)}"
                    @keydown="${this.onKeydown}"
                >
                    ${this.dayRenderer(day, isUnselectable)}
                </time>
            `;
        } catch {
            return html` <div class="c-calendar__day error" aria-label="Error rendering day">${day}</div>`;
        }
    }

    protected isValidForEarliestBookingDate(day: number, month: number, year: number): boolean {
        const bookingDayOffset = Math.max(1, this.bookingDayOffset);

        return !DateUtility.dayIsBeforeMinimalDate(day, month, year, bookingDayOffset);
    }

    getCustomTimeElementClassBuilders(day: number | null, month: number, year: number): ClassBuilder[] {
        if (!day) {
            return [];
        }
        const currentDate = this._formatDate(day, month, year);

        const isToday =
            day === this.today.getDate() && month === this.today.getMonth() + 1 && year === this.today.getFullYear();

        let isSelectedStart = false;
        let isSelectedEnd = false;
        let isSelected = false;
        let isInRange = false;

        if (this.mode === 'single') {
            isSelected = currentDate === this.selectedDate;
        } else {
            isInRange = DateUtility.safeCallDateUtility(
                () => DateUtility.dayIsInRange(day, month, year, this.selectedStartDate, this.selectedEndDate),
                false,
                'dayIsInRange'
            );

            const isSameDay = Boolean(
                this.selectedStartDate &&
                this.selectedEndDate &&
                DateUtility.isValidDateString(this.selectedStartDate) &&
                DateUtility.isValidDateString(this.selectedEndDate) &&
                this.selectedStartDate === this.selectedEndDate
            );

            if (isSameDay) {
                isSelected = DateUtility.safeCallDateUtility(
                    () => DateUtility.dayIsGivenDay(day, month, year, this.selectedStartDate),
                    false,
                    'dayIsGivenDay (same day)'
                );
                isSelectedStart = false;
                isSelectedEnd = false;
            } else {
                isSelectedStart = DateUtility.safeCallDateUtility(
                    () => DateUtility.dayIsGivenDay(day, month, year, this.selectedStartDate),
                    false,
                    'dayIsGivenDay (start)'
                );
                isSelectedEnd = DateUtility.safeCallDateUtility(
                    () => DateUtility.dayIsGivenDay(day, month, year, this.selectedEndDate),
                    false,
                    'dayIsGivenDay (end)'
                );
            }
        }

        return [
            { class: [CalendarTimeElementClass.Day], case: true },
            { class: [CalendarTimeElementClass.Today], case: isToday },
            { class: [CalendarTimeElementClass.Selected], case: isSelected || isSelectedStart || isSelectedEnd },
            { class: [CalendarTimeElementClass.SelectedStart], case: isSelectedStart },
            { class: [CalendarTimeElementClass.SelectedEnd], case: isSelectedEnd },
            {
                class: [CalendarTimeElementClass.Unselectable],
                case: DateUtility.safeCallDateUtility(
                    () => !this.isValidForEarliestBookingDate(day, month, year),
                    false,
                    'dayIsBeforeMinimalDate'
                ),
            },
            {
                class: [CalendarTimeElementClass.Unselectable],
                case: DateUtility.safeCallDateUtility(
                    () =>
                        DateUtility.dayIsClosedDay(day, month, year, this.openingWeekdays, this.specialSelectableDates),
                    false,
                    'dayIsClosedDay'
                ),
            },
            {
                class: [CalendarTimeElementClass.Flexible],
                case: DateUtility.safeCallDateUtility(
                    () => DateUtility.dayIsInRange(day, month, year, this.flexibleStartDate, this.flexibleEndDate),
                    false,
                    'dayIsInRange (flexible)'
                ),
                modifier: [
                    {
                        class: [CalendarTimeElementClass.FlexibleStart],
                        case: DateUtility.safeCallDateUtility(
                            () => DateUtility.dayIsGivenDay(day, month, year, this.flexibleStartDate),
                            false,
                            'dayIsGivenDay (flexible start)'
                        ),
                    },
                    {
                        class: [CalendarTimeElementClass.FlexibleEnd],
                        case: DateUtility.safeCallDateUtility(
                            () => DateUtility.dayIsGivenDay(day, month, year, this.flexibleEndDate),
                            false,
                            'dayIsGivenDay (flexible end)'
                        ),
                    },
                ],
            },
            {
                class: [CalendarTimeElementClass.SelectedRange],
                case: this.mode === 'range' && isInRange && !isSelectedStart && !isSelectedEnd && !isSelected,
            },
        ];
    }

    private renderNavigation(options: SelectOption[]) {
        const lastOption = options[options.length - 1] || { value: '' };
        this.prevDisabled =
            this.activeYear === this.today.getFullYear() && this.activeMonth === this.today.getMonth() + 1;
        this.nextDisabled =
            options.length > 0 && CalendarUtility.getMonthKey(this.activeYear, this.activeMonth) === lastOption.value;

        return html`
            <der-ui-controls
                variant="reduced"
                direction="left"
                @click="${this.prevMonth}"
                class="c-calendar__month-control  c-calendar__month-control--prev"
                aria-label="Previous month"
                ?disabled="${this.prevDisabled}"
            ></der-ui-controls>

            <der-ui-controls
                variant="reduced"
                direction="right"
                @click="${this.nextMonth}"
                class="c-calendar__month-control  c-calendar__month-control--next"
                aria-label="Next month"
                ?disabled="${this.nextDisabled}"
            ></der-ui-controls>
        `;
    }

    private onMonthChange = (e: Event, sheetIndex: number) => {
        try {
            const select = e.target as HTMLSelectElement;
            const parsed = JSON.parse(select.value);

            const newDate = new Date(parsed.year, parsed.month - 1, 1);
            newDate.setMonth(newDate.getMonth() - sheetIndex);

            this.activeMonth = newDate.getMonth() + 1;
            this.activeYear = newDate.getFullYear();
        } catch (error) {
            console.error('Calendar: Error changing month:', error);
        }
    };

    private renderWeekdays() {
        const weekdayOrder = DateUtility.getWeekdayOrder(this.lang);
        return html`
            <div
                class="c-calendar__weekdays ${this.lang === LocaleCode.FR_CH ? '__fr' : ''}"
                role="row"
                aria-label="Calendar weekdays"
            >
                ${weekdayOrder.map(
                    (day) => html`
                        <span
                            class="c-calendar__weekday"
                            role="columnheader"
                            title="${DateUtility.getLocalizedWeekdayName(day, this.lang, 'long')}"
                        >
                            ${DateUtility.getLocalizedWeekdayName(day, this.lang, 'short')}
                        </span>
                    `
                )}
            </div>
        `;
    }

    private renderMonthDays(month: number, year: number) {
        try {
            const firstDayOfWeek = DateUtility.getFirstDayOfWeek(this.lang);
            const firstDayOfMonth = new Date(year, month - 1, 1).getDay();
            const daysOffsetSelectedMonth = (firstDayOfMonth - firstDayOfWeek + 7) % 7;
            const daysCountSelectedMonth = new Date(year, month, 0).getDate();
            const weeksCountSelectedMonth = Math.ceil((daysOffsetSelectedMonth + daysCountSelectedMonth) / 7);
            const daysPerWeek = 7;

            const weeks: (number | null)[][] = [[]];
            let currentDay = 1;

            for (let w = 0; w < weeksCountSelectedMonth; w++) {
                const week = [];
                for (let d = 0; d < daysPerWeek; d++) {
                    if (w * daysPerWeek + d >= daysOffsetSelectedMonth && currentDay <= daysCountSelectedMonth) {
                        week[d] = currentDay;
                        currentDay++;
                    } else {
                        week[d] = null;
                    }
                }
                weeks[w] = week;
            }

            return html`
                <div class="c-calendar__days" role="grid" aria-label="Calendar days">
                    ${weeks.flat().map((day) => this.renderDay(day, month, year))}
                </div>
            `;
        } catch (error) {
            console.error('Calendar: Error rendering month days:', error);
            return html` <div class="c-calendar__days error" aria-label="Calendar days">Error loading calendar</div>`;
        }
    }

    private renderMonths(options: SelectOption[]) {
        const months = [];
        let month: number = this.activeMonth;
        let year: number = this.activeYear;

        for (let i = 0; i < this.sheets; i++) {
            months.push(html`
                <div
                    class="c-calendar__month"
                    role="grid"
                    aria-label="${CalendarUtility.resolveMonthTranslation(this.lang, year, month)} ${year}"
                >
                    <div class="c-calendar__month-title-container">
                        ${this.renderNavigation(options)}
                        <div class="c-calendar__month-title" role="heading" aria-level="2">
                            ${CalendarUtility.resolveMonthTranslation(this.lang, year, month)} ${year}
                            ${
                                this.monthSelectionDropdown
                                    ? html`
                                          <select
                                              @change="${(e: Event) => this.onMonthChange(e, i)}"
                                              .value="${CalendarUtility.getMonthKey(year, month)}"
                                              class="c-calendar__month-selector"
                                              aria-label="Select month and year"
                                          >
                                              ${options.map(
                                                  (option) => html`
                                                      <option value="${option.value}">${option.label}</option>
                                                  `
                                              )}
                                          </select>
                                      `
                                    : ''
                            }
                        </div>
                    </div>
                    ${this.renderWeekdays()} ${this.renderMonthDays(month, year)}
                </div>
            `);
            month = month === 12 ? 1 : month + 1;
            year = month === 1 ? year + 1 : year;
        }

        return html` <div class="c-calendar__months">${months}</div>`;
    }

    override render() {
        try {
            const options = CalendarUtility.getMonthOptions(
                this.todayString,
                this.flexibleStartDate,
                this.monthDropdownLimit,
                this.lang
            );

            return html`
                <div class="c-calendar" data-mode="${this.mode}" role="application" aria-label="Date picker calendar">
                    ${this.renderMonths(options)}
                </div>
            `;
        } catch (error) {
            console.error('Calendar: Error in render:', error);
            return html` <div class="c-calendar error">Calendar could not be loaded</div>`;
        }
    }
}
